import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, setSession } from '@/lib/auth';
import { getAttribution } from '@/lib/attribution';
import { gaEvent } from '@/lib/analytics';
import { useToast } from '@/components/Toast';
import Logo from '@/components/Logo';
import { EyeIcon, EyeOffIcon } from '@/components/icons';
import { useSeo } from '@/lib/seo';

const friendlyError = (err) => {
  if (!err.response) return "Can't reach the server. Check your connection and try again.";
  const status = err.response.status;
  const serverMsg = err.response.data?.error;
  if (status === 409) return 'That mobile number is already registered. Try logging in instead.';
  if (status >= 500) return 'Something went wrong on our end. Please try again in a moment.';
  return serverMsg || 'Registration failed. Please try again.';
};

const passwordStrength = (pw) => {
  if (!pw) return { label: '', width: '0%', color: '' };
  let score = 0;
  if (pw.length >= 6) score++;
  if (pw.length >= 10) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw) && /[^A-Za-z0-9]/.test(pw)) score++;
  const levels = [
    { label: 'Too short', width: '20%', color: 'bg-red-400' },
    { label: 'Weak', width: '40%', color: 'bg-orange-400' },
    { label: 'Okay', width: '65%', color: 'bg-yellow-400' },
    { label: 'Good', width: '85%', color: 'bg-emerald-400' },
    { label: 'Strong', width: '100%', color: 'bg-emerald-500' },
  ];
  return levels[Math.min(score, 4)];
};

const Register = () => {
  useSeo({ title: 'Create account | DotLink', noindex: true });
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [fullName, setFullName] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(false);

  const strength = passwordStrength(password);

  const triggerError = (msg) => {
    setError(msg);
    setShake(true);
    setTimeout(() => setShake(false), 400);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (fullName.trim().length < 2) {
      triggerError('Enter your full name.');
      return;
    }
    if (mobile.trim().length < 10) {
      triggerError('Enter a valid 10-digit mobile number.');
      return;
    }
    if (password.length < 6) {
      triggerError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/register', {
        full_name: fullName.trim(),
        mobile: mobile.trim(),
        password,
        attribution: getAttribution(),     // where this signup came from (UTM / partner ref)
      });
      setSession(response.data.user, response.data.access_token);
      gaEvent('sign_up', { method: 'mobile' });
      showToast(`Welcome to DotLink, ${response.data.user.full_name?.split(' ')[0] || 'there'}!`, 'success');
      navigate((() => { try { const b = sessionStorage.getItem('dl_after_login'); sessionStorage.removeItem('dl_after_login'); return b && b.startsWith('/') ? b : '/dashboard'; } catch { return '/dashboard'; } })());
    } catch (err) {
      triggerError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-72 h-72 bg-purple-300/30 rounded-full blur-3xl" />
      <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-indigo-300/30 rounded-full blur-3xl" />

      <div className="w-full max-w-md relative animate-fade-up">
        <div className="flex flex-col items-center mb-8">
          <Logo size="lg" withText={false} />
          <h1 className="mt-4 text-2xl font-bold text-gray-800">Create your account</h1>
          <p className="text-gray-500 text-sm mt-1">Start protecting your vehicles, assets & pets</p>
        </div>

        <div className={`bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl ring-1 ring-black/5 p-8 ${shake ? 'animate-shake' : ''}`}>
          {error && (
            <div className="flex items-start gap-2 bg-red-50 text-red-700 text-sm rounded-2xl px-4 py-3 mb-5 animate-fade-up">
              <span className="mt-0.5">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Full name</label>
              <div className="relative">
                <input
                  type="text"
                  autoComplete="name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl pl-4 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow"
                  placeholder="Jane Doe"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Mobile number</label>
              <div className="relative">
                <input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/[^\d]/g, '').slice(0, 10))}
                  className="w-full border border-gray-200 rounded-xl pl-4 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow"
                  placeholder="9876543210"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl pl-4 pr-11 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow"
                  placeholder="At least 6 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg px-1"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              {password && (
                <div className="mt-2">
                  <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${strength.color}`}
                      style={{ width: strength.width }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{strength.label}</p>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-press w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl py-3.5 font-semibold shadow-lg shadow-indigo-600/20 disabled:opacity-70 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </button>
            <p className="text-xs text-gray-500 text-center">
              By creating an account you agree to our{' '}
              <Link to="/terms" className="text-indigo-600 underline">Terms</Link> and{' '}
              <Link to="/privacy" className="text-indigo-600 underline">Privacy Policy</Link>.
            </p>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-indigo-600 font-semibold hover:underline">
              Log in
            </Link>
          </p>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6 flex items-center justify-center gap-1.5">
          <span>🔐</span> Your data is encrypted and never shared without consent
        </p>
      </div>
    </div>
  );
};

export default Register;
